import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from 'utils/base-query';
import { getUserAccessToken, getAuthToken } from 'utils/localstorage.helper';

// Define a service using a base URL and expected endpoints
export const campaignsApi = createApi({
  reducerPath: 'campaignsApi',
  tagTypes: ['CAMPAIGNS'],
  baseQuery: baseQuery,
  endpoints: (builder) => ({
    getCampaignsPagination: builder.mutation({
      query: (queryParams) => {
        return {
          method: 'GET',
          url: '/campaigns',
          params: queryParams,
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`,
          },
        };
      },
      providesTags: ['CAMPAIGNS'],
    }),

    createCampaign: builder.mutation({
      query: (data) => {
        return {
          method: 'POST',
          url: '/campaigns',
          body: data,
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`,
          },
        };
      },
      invalidatesTags: ['CAMPAIGNS'],
    }),
    getAccountAnalytics: builder.mutation({
      query: (params) => {
        return {
          method: 'GET',
          url: '/campaigns/analytics',
          params,
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`,
          },
        };
      },
      invalidatesTags: ['ANALYTICS'],
    }),
    getCampaign: builder.query({
      query: (data) => {
        return {
          method: 'GET',
          url: '/campaigns/' + data.id,
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`,
          },
        };
      },
      providesTags: (result, error, arg) => [`CAMPAIGN_${arg}`],
    }),
    getCampaigns: builder.query({
      query: (queryParams) => {
        const params = new URLSearchParams(_.pickBy(queryParams));
        return {
          method: 'GET',
          url: '/campaigns',
          params,
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`,
          },
        };
      },
      providesTags: ['CAMPAIGNS'],
    }),
    getCampaignNames: builder.query({
      query: () => {
        return {
          method: 'GET',
          url: '/campaigns/names',
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`,
          },
        };
      },
    }),
    pauseCampaign: builder.mutation({
      query: ({ id }) => {
        return {
          method: 'PUT',
          url: `/campaigns/${id}/pause`,
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`,
          },
        };
      },
      invalidatesTags: (result, error, arg) => ['CAMPAIGNS', `CAMPAIGN_${arg}`],
    }),
    resumeCampaign: builder.mutation({
      query: ({ id }) => {
        return {
          method: 'PUT',
          url: `/campaigns/${id}/resume`,
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`,
          },
        };
      },
      invalidatesTags: (result, error, arg) => ['CAMPAIGNS', `CAMPAIGN_${arg}`],
    }),
    updateCampaign: builder.mutation({
      query: ({ id, data }) => {
        return {
          method: 'PUT',
          url: `/campaigns/${id}`,
          body: data,
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`,
          },
        };
      },
      invalidatesTags: (result) => [
        'CAMPAIGNS',
        `CAMPAIGN_${result.campaign._id}`,
      ],
    }),
    deleteCampaign: builder.mutation({
      query: (id) => {
        return {
          method: 'DELETE',
          url: `/campaigns/${id}`,
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`,
          },
        };
      },
      invalidatesTags: ['CAMPAIGNS'],
    }),
    createCampaignSchedule: builder.mutation({
      query: ({ id, data }) => {
        return {
          method: 'POST',
          url: `/campaigns/${id}/schedules`,
          body: data,
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`,
          },
        };
      },
      invalidatesTags: (result, error, arg) => [`CAMPAIGN_${arg.id}`],
    }),
    updateCampaignSchedule: builder.mutation({
      query: ({ id, data }) => {
        return {
          method: 'PUT',
          url: `/campaigns/schedules/${id}`,
          body: data,
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`,
          },
        };
      },
      invalidatesTags: (result) => [`CAMPAIGN_${result.schedule.campaign}`],
    }),
    deleteCampaignSchedule: builder.mutation({
      query: (id) => {
        return {
          method: 'DELETE',
          url: `/campaigns/schedules/${id}`,
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`,
          },
        };
      },
      invalidatesTags: (result) => [`CAMPAIGN_${result.schedule.campaign}`],
    }),
    writeEmail: builder.mutation({
      query: (data) => {
        return {
          method: 'POST',
          url: `/campaigns/write_email`,
          body: data,
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`,
          },
        };
      },
      transformResponse: (response) => response.body,
    }),
    optimizeEmail: builder.mutation({
      query: (data) => {
        return {
          method: 'POST',
          url: `/campaigns/optimize_email`,
          body: data,
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`,
          },
        };
      },
      transformResponse: (response) => response.body,
    }),
    updateConfigurations: builder.mutation({
      query: (data) => {
        return {
          method: 'POST',
          url: `/campaigns/set/options`,
          body: data,
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`,
          },
        };
      },
    }),
    createLeads: builder.mutation({
      query: ({ id, data }) => {
        return {
          method: 'POST',
          url: `/campaigns/${id}/leads`,
          body: data,
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`,
          },
        };
      },
    }),
    getLeads: builder.mutation({
      query: ({ id, params }) => {
        return {
          method: 'GET',
          url: `/campaigns/${id}/leads`,
          params,
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`,
          },
        };
      },
    }),
    createSequence: builder.mutation({
      query: ({ id, data }) => {
        return {
          method: 'POST',
          url: `/campaigns/${id}/sequences`,
          body: data,
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`,
          },
        };
      },
      invalidatesTags: (result) => [`CAMPAIGN_${result?.sequence?.campaign}`],
    }),
    updateSequenceOrder: builder.mutation({
      query: ({ id, data }) => {
        return {
          method: 'PUT',
          url: `/campaigns/${id}/sequences/order`,
          body: data,
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`,
          },
        };
      },
      invalidatesTags: (result, error, arg) => [`CAMPAIGN_${arg.id}`],
    }),
    updateSequence: builder.mutation({
      query: ({ id, data }) => {
        return {
          method: 'PUT',
          url: `/campaigns/sequences/${id}`,
          body: data,
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`,
          },
        };
      },
      invalidatesTags: (result) => [`CAMPAIGN_${result.sequence.campaign}`],
    }),
    copySequence: builder.mutation({
      query: ({ id }) => {
        return {
          method: 'POST',
          url: `/campaigns/sequences/${id}/copy`,
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`,
          },
        };
      },
      invalidatesTags: (result) => [`CAMPAIGN_${result.sequence.campaign}`],
    }),
    deleteSequence: builder.mutation({
      query: ({ id }) => {
        return {
          method: 'DELETE',
          url: `/campaigns/sequences/${id}`,
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`,
          },
        };
      },
      invalidatesTags: (result) => [`CAMPAIGN_${result.sequence.campaign}`],
    }),
    getCampaignVariables: builder.query({
      query: (id) => {
        return {
          method: 'GET',
          url: `/campaigns/get-variables/${id}`,
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`,
          },
        };
      },
      transformResponse: (response) => response.variables,
    }),
    campaignLaunch: builder.mutation({
      query: ({ id }) => {
        return {
          method: 'POST',
          url: `/campaigns/launch/${id}`,
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`,
          },
        };
      },
    }),
    campaignAnalytics: builder.mutation({
      query: ({ id, params }) => {
        return {
          method: 'GET',
          url: `/campaigns/get_campaign_analytics/${id}`,
          params,
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`,
          },
        };
      },
    }),
    renameCampaign: builder.mutation({
      query: ({ id, params }) => {
        return {
          method: 'PUT',
          url: `${process.env.REACT_APP_INTERCOM_PROXY_BASE_URL}campaigns/update_campaign/${id}`,
          body: params,
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`,
          },
        };
      },
    }),
    campaignAnalyticsGraphData: builder.mutation({
      query: ({ id, params }) => {
        return {
          method: 'GET',
          url: `/campaigns/${id}/analytics`,
          params,
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`,
          },
        };
      },
    }),
    analytics: builder.mutation({
      query: (queryParams) => {
        const params = new URLSearchParams(_.pickBy(queryParams));
        return {
          method: 'GET',
          url: `/campaigns/analytics/stats`,
          params,
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`,
          },
        };
      },
      providesTags: ['ANALYTICS'],
    }),
    createLabel: builder.mutation({
      query: (data) => {
        return {
          method: 'POST',
          url: '/campaigns/create/label',
          body: data,
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`,
          },
        };
      },
    }),
    getAllLabels: builder.query({
      query: () => {
        return {
          method: 'GET',
          url: '/campaigns/get/label',
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`,
          },
        };
      },
    }),
    updateLabel: builder.mutation({
      query: ({ campaignEmailId, labelId }) => {
        return {
          method: 'PUT',
          url: `/campaigns/updatelabel/${campaignEmailId}/${labelId}`,
          headers: {
            authorization: `Bearer ${getUserAccessToken()}`,
          },
        };
      },
    }),
    emailAccounts: builder.mutation({
      query: (data) => {
        const params = new URLSearchParams(_.pickBy(data));
        return {
          method: 'GET',
          url: '/admin/email-accounts',
          params,
          headers: {
            authorization: `Bearer ${getAuthToken()}`,
          },
        };
      },
    }),
    multichannelAccounts: builder.mutation({
      query: (data) => {
        const params = new URLSearchParams(_.pickBy(data));
        return {
          method: 'GET',
          url: '/admin/multichannel-accounts',
          params,
          headers: {
            authorization: `Bearer ${getAuthToken()}`,
          },
        };
      },
    }),
    updateEmailAccounts: builder.mutation({
      query: ({ data }) => {
        return {
          method: 'POST',
          url: '/admin/update-email-accounts',
          body: data,
          headers: {
            authorization: `Bearer ${getAuthToken()}`,
          },
        };
      },
    }),
    exportEmailAccounts: builder.mutation({
      query: (data) => {
        const params = new URLSearchParams(_.pickBy(data));
        return {
          method: 'GET',
          url: '/admin/export-email-accounts',
          params,
          headers: {
            authorization: `Bearer ${getAuthToken()}`,
          },
        };
      },
    }),
    emailAccountsAnalytics: builder.mutation({
      query: (data) => {
        const params = new URLSearchParams(_.pickBy(data));
        return {
          method: 'GET',
          url: '/admin/email-account-analytics',
          params,
          headers: {
            authorization: `Bearer ${getAuthToken()}`,
          },
        };
      },
    }),
    emailDKIM: builder.mutation({
      query: (data) => {
        return {
          method: 'POST',
          url: '/admin/email-dkim-data',
          body: data,
          headers: {
            authorization: `Bearer ${getAuthToken()}`,
          },
        };
      },
    }),
    updateWarmupBulk: builder.mutation({
      query: ({ data }) => {
        return {
          method: 'POST',
          url: '/admin/warmup/bulk',
          body: data,
          headers: {
            authorization: `Bearer ${getAuthToken()}`,
          },
        };
      },
    }),
    bulkResumeAccounts: builder.mutation({
      query: ({ data }) => {
        return {
          method: 'POST',
          url: '/admin/accounts/bulk-resume',
          body: data,
          headers: {
            authorization: `Bearer ${getAuthToken()}`,
          },
        };
      },
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetCampaignsPaginationMutation,
  useCreateCampaignMutation,
  useGetAccountAnalyticsMutation,
  useLazyGetCampaignQuery,
  useGetCampaignsQuery,
  useLazyGetCampaignNamesQuery,
  usePauseCampaignMutation,
  useResumeCampaignMutation,
  useUpdateCampaignMutation,
  useDeleteCampaignMutation,
  useCreateLeadsMutation,
  useGetLeadsMutation,
  useCreateCampaignScheduleMutation,
  useUpdateCampaignScheduleMutation,
  useDeleteCampaignScheduleMutation,
  useWriteEmailMutation,
  useUpdateConfigurationsMutation,
  useOptimizeEmailMutation,
  useCreateSequenceMutation,
  useUpdateSequenceMutation,
  useUpdateSequenceOrderMutation,
  useCopySequenceMutation,
  useDeleteSequenceMutation,
  useCampaignLaunchMutation,
  useGetCampaignVariablesQuery,
  useCampaignAnalyticsMutation,
  useCampaignAnalyticsGraphDataMutation,
  useAnalyticsMutation,
  useRenameCampaignMutation,
  useCreateLabelMutation,
  useGetAllLabelsQuery,
  useUpdateLabelMutation,
  useEmailAccountsMutation,
  useMultichannelAccountsMutation,
  useUpdateEmailAccountsMutation,
  useExportEmailAccountsMutation,
  useEmailAccountsAnalyticsMutation,
  useEmailDKIMMutation,
  useUpdateWarmupBulkMutation,
  useBulkResumeAccountsMutation,
} = campaignsApi;
